import React, { useState } from 'react'
import { projectStatusEnum } from '~/common/types/enum-dictionaries'

import { StatusEnumType } from 'shared/common/global-types.runtime'
import ReasonDialog from '~/views/components/projects/ProjectStatusChangeReasons/ReasonDialog/ReasonDialog'

import { useFlashMessageContext } from '~/views/contexts/FlashMessageContext'
import { useCreateProjectStatusChangeReason } from '~/views/hooks/projects/useCreateProjectStatusChangeReason'
import { useGetStatusChangeReasonGroups } from '~/views/hooks/statusChangeReasonGroup/useGetStatusChangeReasonGroups'

interface AddProjectReasonDialogProps {
  newProjectStatus?: StatusEnumType
  onClose(): void
  onSubmit?(): void
  open: boolean
  organizationId?: string | null
  project: {
    id: string
    status: StatusEnumType
  }
}

const AddProjectReasonDialog = ({
  newProjectStatus,
  onClose,
  onSubmit,
  open,
  organizationId,
  project,
}: AddProjectReasonDialogProps) => {
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [selectedReasonId, setSelectedReasonId] = useState('')
  const [description, setDescription] = useState('')
  const [projectStatusChangeReasonsCreateMutation, { loading }] =
    useCreateProjectStatusChangeReason()
  const { showFlashMessage } = useFlashMessageContext()
  const orgId = organizationId || ''
  const { data } = useGetStatusChangeReasonGroups(orgId)

  const getStatusForSubtitle = () => {
    const selectedStatus = projectStatusEnum.find((enumStatus) => {
      if (newProjectStatus) {
        return enumStatus.value === newProjectStatus
      } else {
        return enumStatus.value === project?.status
      }
    })

    const delayedStatuses = selectedStatus
      ? selectedStatus?.displayName
      : 'Late, On Hold, or Cancelled'

    return delayedStatuses
  }

  const title = 'Status Change Reason Required'
  const subTitle = (
    <div>
      The project status is being updated to <b>{getStatusForSubtitle()}</b>.
      Please add a reason why it needs to be changed in order to save this
      update.
    </div>
  )

  const onSave = async () => {
    try {
      await projectStatusChangeReasonsCreateMutation({
        variables: {
          input: {
            projectId: project?.id,
            description: description ? description : '',
            statusChangeReasonId: selectedReasonId,
          },
        },
        update: (cache, results) => {
          const newProjectStatusReason =
            results.data?.createProjectStatusChangeReason
              ?.projectStatusChangeReason

          cache.modify({
            id: cache.identify({
              __typename: 'ProjectType',
              id: project?.id,
            }),
            fields: {
              projectStatusChangeReasons(existingProjectStatusReasons = []) {
                return [...existingProjectStatusReasons, newProjectStatusReason]
              },
            },
          })
        },
        refetchQueries: ['Projects_ProjectList'],
      })

      if (onSubmit) onSubmit()
      onClose()
    } catch (error) {
      showFlashMessage({ error })
    }
  }

  return (
    <ReasonDialog
      title={title}
      subTitle={subTitle}
      open={open}
      loading={loading}
      onClose={onClose}
      onSave={onSave}
      status={project?.status}
      selectedGroupId={selectedGroupId}
      setSelectedGroupId={setSelectedGroupId}
      selectedReasonId={selectedReasonId}
      setSelectedReasonId={setSelectedReasonId}
      reasonGroups={data?.statusChangeReasonGroupConnections}
      description={description}
      setDescription={setDescription}
      disabled={!selectedGroupId || !selectedReasonId}
      isAddReason={true}
    />
  )
}

export default AddProjectReasonDialog
